// «جوّه الريّس». Two full-bleed rows of real product screens that slide in
// opposite directions as the page scrolls, and a rail that advances on its own on
// phones. REFERENCE.md §11, spec section 6.
//
// The spec says auto-scrolling and the reference has no time-based motion at all:
// the rows sit perfectly still when the page does. The owner asked for the same
// page, so this follows the reference (REFERENCE.md §17 item 3). Under reduced
// motion the rows do not move.
//
// product-customers.png must never appear here: it shows real-looking phone numbers.

import { useCallback, useEffect, useRef, useState } from "react";
import SectionHead from "./SectionHead";
import { Capture, CAPTURES } from "./media";
import { INSIDE } from "./copy";
import { FINE_POINTER, onScrollFrame, useMediaQuery, useMotionAllowed } from "./motion/env";
import useAutoRail from "./motion/useAutoRail";
import useSectionProgress from "./motion/useSectionProgress";
import "./Marquee.css";

const DRIFT_QUERY = "(min-width: 768px)";
const DRIFT_PER_PX = 0.35; // sideways per pixel scrolled
const DRIFT_EASE = 0.1; // of the remaining distance, per frame

const LABEL_EASE = 0.14;
const LABEL_TILT = 0.12; // degrees per pixel still to travel
const LABEL_TILT_MAX = 8;
const LABEL_TILT_EASE = 0.2;
const LABEL_EDGE = 56; // px kept clear of the row's left edge

const ratioOf = (item) => {
    const c = CAPTURES[item.capture];
    return c ? c.width / c.height : 1.6;
};

const half = Math.ceil(INSIDE.items.length / 2);
const ROW_ONE = INSIDE.items.slice(0, half);
const ROW_TWO = INSIDE.items.slice(half);

// One card. Copies repeat the row so its ends are never seen; they are hidden
// from assistive tech, and only the originals carry an alt.
function Card({ item, i, copy }) {
    return (
        <li
            className="ld-cert"
            style={{ "--i": i, "--ld-cert-ar": ratioOf(item) }}
            data-kicker={item.kicker}
            data-name={item.name}
            aria-hidden={copy ? "true" : undefined}
        >
            <Capture
                name={item.capture}
                className="ld-cert__img"
                sizes="(min-width: 1024px) 27vw, 27rem"
                alt={copy ? "" : `${item.kicker}: ${item.name}`}
            />
        </li>
    );
}

// x = direction × P × travel, where travel is .35px per pixel scrolled across the
// row's whole journey, limited to what the track has beyond the edge it is
// moving away from. Each frame closes 10% of the distance.
function useRowDrift(rowRef, trackRef, direction) {
    const state = useRef({ x: 0, target: 0, raf: 0, started: false });

    useEffect(() => {
        const s = state.current;
        return () => cancelAnimationFrame(s.raf);
    }, []);

    const onFrame = useCallback((p) => {
        const row = rowRef.current;
        const track = trackRef.current;
        if (!row || !track) return;
        const s = state.current;

        const rowBox = row.getBoundingClientRect();
        const trackBox = track.getBoundingClientRect();
        const beyond = direction < 0
            ? trackBox.right - s.x - rowBox.right
            : rowBox.left - (trackBox.left - s.x);
        const travel = Math.min(DRIFT_PER_PX * (window.innerHeight + rowBox.height), Math.max(beyond, 0));
        s.target = direction * p * travel;

        const paint = () => {
            s.raf = 0;
            s.x += (s.target - s.x) * DRIFT_EASE;
            if (Math.abs(s.target - s.x) < 0.1) s.x = s.target;
            track.style.transform = `translate3d(${s.x.toFixed(2)}px, 0, 0)`;
            if (s.x !== s.target) s.raf = requestAnimationFrame(paint);
        };

        // The row is already in place the first time it is measured.
        if (!s.started) {
            s.started = true;
            s.x = s.target;
            track.style.transform = `translate3d(${s.x.toFixed(2)}px, 0, 0)`;
            return;
        }
        if (!s.raf) s.raf = requestAnimationFrame(paint);
    }, [rowRef, trackRef, direction]);

    useSectionProgress(rowRef, { query: DRIFT_QUERY, property: "--ld-row-p", onFrame });
}

// `lead` is how many sets sit beyond the row's start before the originals.
function Row({ items, direction, lead, sets, className = "" }) {
    const rowRef = useRef(null);
    const trackRef = useRef(null);
    useRowDrift(rowRef, trackRef, direction);

    const leadRatio = lead * items.reduce((sum, item) => sum + ratioOf(item), 0);
    const leadCount = lead * items.length;

    return (
        <div className={`ld-inside__row ${className}`.trim()} ref={rowRef} data-ld="certs" data-ld-early="">
            <ul
                className="ld-inside__track"
                ref={trackRef}
                style={{ "--ld-lead-ar": leadRatio, "--ld-lead-n": leadCount }}
            >
                {Array.from({ length: sets }, (_, set) => items.map((item, i) => (
                    <Card key={`${set}-${item.capture}`} item={item} i={i} copy={set !== lead} />
                )))}
            </ul>
        </div>
    );
}

// The glass card that follows the pointer over the rows and names the screen under it.
function useCursorLabel(motion) {
    const areaRef = useRef(null);
    const labelRef = useRef(null);
    const [shown, setShown] = useState(null);
    const fine = useMediaQuery(FINE_POINTER);

    useEffect(() => {
        const area = areaRef.current;
        const label = labelRef.current;
        if (!area || !label || !fine) return undefined;

        const ease = motion ? LABEL_EASE : 1;
        let tx = 0;
        let ty = 0;
        let x = 0;
        let y = 0;
        let tilt = 0;
        let raf = 0;
        let on = false;
        let inside = false;
        let cx = 0;
        let cy = 0;

        const paint = () => {
            x += (tx - x) * ease;
            y += (ty - y) * ease;
            const lean = motion
                ? Math.max(-LABEL_TILT_MAX, Math.min(LABEL_TILT_MAX, (tx - x) * LABEL_TILT))
                : 0;
            tilt += (lean - tilt) * LABEL_TILT_EASE;
            label.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${tilt}deg)`;
            const moving = Math.abs(tx - x) + Math.abs(ty - y) + Math.abs(tilt) > 0.05;
            raf = moving ? requestAnimationFrame(paint) : 0;
        };

        // Re-read on scroll as well, because the rows move under a still pointer.
        const update = () => {
            const box = area.getBoundingClientRect();
            tx = cx - box.left;
            ty = cy - box.top;
            const w = label.firstElementChild?.offsetWidth || 0;
            // Sits left of the pointer, and crosses over only near the left edge.
            label.classList.toggle("is-right", tx - w - LABEL_EDGE <= 0);

            const hit = document.elementFromPoint(cx, cy)?.closest(".ld-cert");
            const card = hit && area.contains(hit) ? hit : null;
            if (card && !on) {
                x = tx;
                y = ty;
                tilt = 0;
            }
            on = !!card;
            label.classList.toggle("is-on", on);
            if (card) {
                const next = { kicker: card.dataset.kicker, name: card.dataset.name };
                setShown((cur) => (cur && cur.name === next.name ? cur : next));
            }
            if (!raf) raf = requestAnimationFrame(paint);
        };

        const onMove = (e) => {
            cx = e.clientX;
            cy = e.clientY;
            inside = true;
            update();
        };

        const onLeave = () => {
            inside = false;
            on = false;
            label.classList.remove("is-on");
        };

        const offScroll = onScrollFrame(() => {
            if (inside) update();
        });

        area.addEventListener("pointermove", onMove, { passive: true });
        area.addEventListener("pointerleave", onLeave);
        return () => {
            area.removeEventListener("pointermove", onMove);
            area.removeEventListener("pointerleave", onLeave);
            offScroll();
            cancelAnimationFrame(raf);
        };
    }, [fine, motion]);

    return { areaRef, labelRef, shown, fine };
}

export default function Marquee() {
    const motion = useMotionAllowed();
    const railRef = useRef(null);
    useAutoRail(railRef);
    const { areaRef, labelRef, shown, fine } = useCursorLabel(motion);

    return (
        <section className="ld-section ld-section--day ld-inside" id="inside" aria-labelledby="ld-inside-title">
            <div className="ld-container">
                <SectionHead
                    id="ld-inside-title"
                    title={INSIDE.title}
                    lead={INSIDE.lead}
                    aside={<a className="ld-ghost" href="#modules">{INSIDE.aside}</a>}
                />
            </div>

            <div className="ld-inside__rows" ref={areaRef}>
                <Row items={ROW_ONE} direction={-1} lead={1} sets={3} className="ld-inside__row--one" />
                <Row items={ROW_TWO} direction={1} lead={0} sets={3} className="ld-inside__row--two" />
                {fine ? (
                    <div className="ld-certlabel" ref={labelRef} aria-hidden="true">
                        <div className="ld-certlabel__card">
                            {shown ? (
                                <>
                                    <span className="ld-certlabel__kicker">{shown.kicker}</span>
                                    <span className="ld-certlabel__name" key={shown.name}>
                                        {shown.name.split(/\s+/).map((word, i) => (
                                            <span className="ld-certlabel__word" key={`${word}-${i}`} style={{ "--i": i }}>
                                                {word}
                                            </span>
                                        ))}
                                    </span>
                                </>
                            ) : null}
                        </div>
                    </div>
                ) : null}
            </div>

            <ul className="ld-rail ld-inside__rail" ref={railRef} data-ld="certs" data-ld-early="">
                {INSIDE.items.map((item, i) => (
                    <li className="ld-inside__slide" key={item.capture} style={{ "--ld-cert-ar": ratioOf(item) }}>
                        <div className="ld-cert ld-cert--slide" style={{ "--i": i }}>
                            <Capture name={item.capture} className="ld-cert__img" sizes="min(100vw, 22rem)" alt="" />
                        </div>
                        <p className="ld-inside__cap">
                            <span className="ld-inside__cap-kicker">{item.kicker}</span>
                            <span>{item.name}</span>
                        </p>
                    </li>
                ))}
            </ul>
        </section>
    );
}
